import React, { useEffect, useState } from "react";
import { Table, Spinner, Button } from "react-bootstrap";
import Swal from "sweetalert2";
import { eventService } from "../lib/api/event";

type FailedEmail = {
  email?: string;
  recipient?: string;
  messageId?: string;
  organizationName?: string;
  doctorName?: string;
  contactNumber?: string;
  eventName?: string;
  error?: string;
  responseMessage?: string;
  status?: string;
  createdAt?: string;
  [key: string]: any;
};

const DISPLAY_COLUMNS = [
  "organizationName",
  "eventName",
  "doctorName",
  "contactNumber",
  "recipient",
  "status",
  "responseMessage",
  "createdAt",
];

const FailedEmails: React.FC = () => {
  const [rows, setRows] = useState<FailedEmail[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    const fetchFailedEmails = async () => {
      setLoading(true);
      try {
        const response = await eventService.getFailedEmails();
        const list = Array.isArray(response?.data)
          ? response.data
          : Array.isArray((response as any)?.data?.data)
          ? (response as any).data.data
          : Array.isArray(response)
          ? response
          : [];

        setRows(list);
        setSelectedIds([]);
      } catch (err) {
        console.error("Failed to load failed emails", err);
        Swal.fire({
          title: "Error",
          text: "Unable to fetch failed emails. Please try again later.",
          icon: "error",
          confirmButtonText: "OK",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchFailedEmails();
  }, []);

  const toggleSelect = (messageId?: string) => {
    if (!messageId) return;
    setSelectedIds((prev) =>
      prev.includes(messageId) ? prev.filter((id) => id !== messageId) : [...prev, messageId]
    );
  };

  const toggleSelectAll = () => {
    const allIds = rows.map((r) => r.messageId).filter((id): id is string => Boolean(id));
    const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : allIds);
  };

  const handleDelete = async () => {
    if (selectedIds.length === 0) return;
    const confirm = await Swal.fire({
      title: "Delete selected failed emails?",
      text: `You are about to delete ${selectedIds.length} item(s).`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
    });
    if (!confirm.isConfirmed) return;

    try {
      setLoading(true);
      // Cast to allow recent API addition without tsserver cache issues
      await (eventService as any).deleteFailedEmails(selectedIds);
      setRows((prev) => prev.filter((row) => !row.messageId || !selectedIds.includes(row.messageId)));
      setSelectedIds([]);
      Swal.fire({
        title: "Deleted",
        text: "Selected failed emails have been removed.",
        icon: "success",
        confirmButtonText: "OK",
      });
    } catch (err) {
      console.error("Failed to delete failed emails", err);
      Swal.fire({
        title: "Error",
        text: "Unable to delete selected emails. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <h3 className="mb-3 text-center cme-header">Failed Emails</h3>
      <div className="d-flex justify-content-end mb-3">
        <Button
          variant="danger"
          size="sm"
          disabled={selectedIds.length === 0 || loading}
          onClick={handleDelete}
        >
          Delete Selected
        </Button>
      </div>
      {loading ? (
        <div className="d-flex justify-content-center my-5">
          <Spinner animation="border" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center text-muted py-4 border rounded bg-white shadow-sm">
          No failed emails found.
        </div>
      ) : (
        <div className="table-responsive shadow-sm rounded">
          <Table bordered hover responsive className="mb-0 align-middle">
            <thead className="table-primary">
              <tr>
                <th style={{ width: 50 }}>
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={
                      rows.length > 0 &&
                      rows
                        .map((r) => r.messageId)
                        .filter((id): id is string => Boolean(id))
                        .every((id) => selectedIds.includes(id))
                    }
                    onChange={toggleSelectAll}
                  />
                </th>
                {DISPLAY_COLUMNS.map((col) => (
                  <th key={col} className="text-capitalize">
                    {col.replace(/([A-Z])/g, " $1")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const renderCell = (col: string) => {
                  if (col === "recipient") {
                    return row.recipient ?? row.email ?? "-";
                  }
                  if (col === "responseMessage") {
                    return row.responseMessage ?? row.error ?? "-";
                  }
                  return row[col] ?? "-";
                };
                return (
                  <tr key={idx}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label="Select row"
                        checked={row.messageId ? selectedIds.includes(row.messageId) : false}
                        onChange={() => toggleSelect(row.messageId)}
                      />
                    </td>
                    {DISPLAY_COLUMNS.map((col) => (
                      <td key={col}>{renderCell(col)}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default FailedEmails;
